import type { Filter } from "@/shared/components/ResourceView";
import type { AdminFeedbackFilters } from "@/shared/domain/admin/hooks/useAdminFeedback";
import type {
  FeedbackCategory,
  FeedbackSentiment,
} from "@/shared/domain/agentThread";

/**
 * Pull the committed admin-feedback filter set out of the ResourceView filter
 * chips. Each feedback field maps to a single equality predicate (the endpoint
 * has no OR/exclusion surface), so a field with anything other than exactly one
 * compatible chip resolves to "unset".
 *
 * The endpoint accepts only positive, exact predicates, so unsupported
 * comparators must resolve to "unset" rather than be coerced: an enum chip is
 * honored only on ``CONTAINS`` (``NOT_CONTAINS`` would silently return the
 * complement) and a ``thread_id`` chip only on ``EQUALS`` (any other
 * comparator would be sent as exact equality). The filter UI already restricts
 * these fields to the supported comparators; this read-side guard keeps the
 * mapping correct even if a chip arrives via a deep link or future UI change.
 */
export function filtersToFeedbackFilters(
  filters: Filter[],
): AdminFeedbackFilters {
  const enumSingle = (field: string): string | undefined => {
    const matches = filters.filter((f) => f.field === field);
    const f = matches.length === 1 ? matches[0] : undefined;
    return f?.type === "enum" &&
      f.comparator === "CONTAINS" &&
      f.values.length === 1
      ? f.values[0]
      : undefined;
  };
  const stringSingle = (field: string): string | undefined => {
    const matches = filters.filter((f) => f.field === field);
    const f = matches.length === 1 ? matches[0] : undefined;
    // Only ``EQUALS`` maps to the endpoint's exact-match param; any other
    // comparator (the filter UI is locked to ``EQUALS``, but guard anyway)
    // would otherwise be silently coerced to equality. Inequality (e.g.
    // "Session ≠ X") is a deliberate non-goal here — the feedback GET endpoint
    // has no exclusion surface; supporting it was deferred, not overlooked.
    return f?.type === "string" &&
      f.comparator === "EQUALS" &&
      f.values.length === 1
      ? f.values[0]
      : undefined;
  };
  const boolVal = (field: string): boolean | undefined => {
    const matches = filters.filter((f) => f.field === field);
    const f = matches.length === 1 ? matches[0] : undefined;
    if (f?.type !== "boolean") {
      return undefined;
    }
    if (f.comparator === "EXISTS") {
      return true;
    }
    if (f.comparator === "NOT_EXISTS") {
      return false;
    }
    return f.values.length === 1 ? f.values[0] : undefined;
  };

  return {
    sentiment: enumSingle("sentiment") as FeedbackSentiment | undefined,
    category: enumSingle("category") as FeedbackCategory | undefined,
    orgId: enumSingle("org_id"),
    createdBy: enumSingle("created_by"),
    sessionId: stringSingle("thread_id"),
    resolved: boolVal("resolved"),
    hasNotes: boolVal("has_notes"),
    hasErrors: boolVal("has_errors"),
    isCustomer: boolVal("is_customer"),
  };
}
